import { useCallback, useEffect, useRef } from "react";
import { Check } from "lucide-react";
import { api } from "../../lib/api";
import { fmt } from "../../lib/format";
import { useMarket } from "../../lib/store";
import { useCommunity, markPrompted, signedPct } from "../../lib/community";
import { Scrim, CloseX, Confetti, useEscape } from "./Overlay";
import { TgBadge } from "./TgBits";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function waitForOrderFormClosed() {
  for (let i = 0; i < 200; i += 1) {
    if (!useMarket.getState().order) return true;
    await sleep(300);
  }
  return false;
}

function useFillListener() {
  useEffect(() => {
    const onFill = async (e) => {
      const o = e.detail;
      if (o?.status !== "COMPLETE" || !markPrompted(o.id)) return;
      await sleep(1200);
      let card;
      try { card = (await api.get(`/community/order/${o.id}`)).data; } catch (err) { return; }
      useCommunity.getState().load();
      const type = card.kind === "trade" ? "filled" : card.kind === "win" ? (card.celebrate === "modal" ? "win" : "profit") : null;
      if (!type || !(await waitForOrderFormClosed())) return;
      const st = useCommunity.getState();
      if (!st.sheet) st.setPrompt({ type, card });
    };
    window.addEventListener("pd:fill", onFill);
    return () => window.removeEventListener("pd:fill", onFill);
  }, []);
}

function Toast({ children, onClose, testid }) {
  const ref = useRef(null);
  useEscape(onClose);
  useEffect(() => {
    const t = setTimeout(onClose, 10000);
    const out = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("pointerdown", out);
    return () => { clearTimeout(t); document.removeEventListener("pointerdown", out); };
  }, [onClose]);
  return (
    <div ref={ref} className="gw gw-toast" role="status" data-testid={testid}>
      <div className="body"><TgBadge size={40} /><div className="min-w-0 flex-1">{children}</div><CloseX onClick={onClose} testid={`${testid}-close`} /></div>
      <div className="bar" />
    </div>
  );
}

function WinModal({ card, onClose, onShare }) {
  return (
    <>
      <Confetti />
      <Scrim onClose={onClose} testid="win-modal" width={440}>
        <div className="gw-ring"><div className="inn text-center" style={{ padding: 22 }}>
          <div className="flex justify-end -mb-6"><CloseX onClick={onClose} testid="win-modal-close" /></div>
          <div className="gw-check"><Check className="h-8 w-8" strokeWidth={3} /></div>
          <div className="gw-eb mt-3.5">Profit booked · {card.symbol}</div>
          <div className="gw-d gw-goldtext text-[52px] mt-2 leading-none" data-testid="win-modal-pnl">+₹{fmt(card.pnl)}</div>
          <div className="gw-mute mt-2" data-testid="win-modal-pct">{signedPct(card.pct)} · exit ₹{fmt(card.exit)}</div>
          <CommunityLine />
          <div className="grid gap-2 mt-3.5">
            <button type="button" className="gw-btn gw-full" onClick={onShare} data-testid="win-modal-share-button">Share this win on Telegram</button>
            <button type="button" className="gw-btn gh gw-full" onClick={onClose} data-testid="win-modal-keep-trading">Keep trading</button>
          </div>
        </div></div>
      </Scrim>
    </>
  );
}

function CommunityLine() {
  const j = useCommunity((s) => s.info?.joined_label);
  return <p className="gw-mute text-[13px] mt-3">{j ? `${j} traders are in the Greenwick community.` : "Traders are sharing in the Greenwick community."} Want to share this one?</p>;
}

export function TradePrompts() {
  useFillListener();
  const prompt = useCommunity((s) => s.prompt);
  const close = useCallback(() => useCommunity.getState().setPrompt(null), []);
  if (!prompt) return null;
  const { type, card } = prompt;
  const share = () => useCommunity.getState().openSheet({ card });
  if (type === "win") return <WinModal card={card} onClose={close} onShare={share} />;
  if (type === "profit") {
    return (
      <Toast onClose={close} testid="profit-toast">
        <b className="text-[14px]">Closed in profit: +₹{fmt(card.pnl)} ({signedPct(card.pct)})</b>
        <p className="gw-mute text-[12.5px] mt-1">{card.symbol} · you exited while the trade was running green.</p>
        <button type="button" className="gw-btn mt-2.5" onClick={share} data-testid="profit-toast-share-button">Share this win on Telegram</button>
      </Toast>
    );
  }
  return (
    <Toast onClose={close} testid="fill-toast">
      <b className="text-[14px] block" data-testid="fill-toast-title">Order filled · {card.side} {card.symbol}</b>
      <p className="gw-mute text-[12.5px] mt-1 gw-m" data-testid="fill-toast-detail">{card.qty} qty @ ₹{fmt(card.entry)}{card.sl ? ` · SL ₹${fmt(card.sl)}` : ""}</p>
      <button type="button" className="gw-btn mt-2.5" onClick={share} data-testid="fill-toast-share-button">Share this trade on Telegram</button>
      <p className="text-[11px] mt-2" style={{ color: "var(--gw-dim)" }}>Optional. Shows entry and SL only.</p>
    </Toast>
  );
}
